import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

class AccordionCustom extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Animation | null} */
  #animation = null;

  #expanded = false;
  #groupName = null;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('Details element not found');
    }

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) {
      throw new Error('Summary element not found');
    }

    return summary;
  }

  get #isProductAccordion() {
    return this.hasAttribute('data-tmc-accordion-group');
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    if (this.#isProductAccordion) {
      const details = this.details;

      // Mémorise le groupe même si le composant est reconnecté.
      if (!details.hasAttribute('data-tmc-original-name')) {
        details.setAttribute(
          'data-tmc-original-name',
          details.getAttribute('name') || ''
        );
      }

      this.#groupName = details.getAttribute('data-tmc-original-name');
      this.#expanded = details.open;

      // La fermeture du précédent est pilotée par notre animation.
      details.removeAttribute('name');
      details.classList.add('tmc-accordion-animated');

      details.addEventListener('toggle', this.#handleToggle, { signal });
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    this.summary.addEventListener('click', this.handleClick, { signal });

    mediaQueryLarge.addEventListener(
      'change',
      this.#handleMediaQueryChange,
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#cancelAnimation();

    if (this.#isProductAccordion) {
      this.details.open = this.#expanded;
    }
  }

  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();

    if (
      (isMobile && this.#disableOnMobile) ||
      (!isMobile && this.#disableOnDesktop)
    ) {
      event.preventDefault();
      return;
    }

    // Les autres accordéons conservent leur fonctionnement natif.
    if (!this.#isProductAccordion) return;

    event.preventDefault();

    const expand = !this.#expanded;

    if (expand) {
      this.#closeOtherRows();
    }

    this.#animateTo(expand);
  };

  #closeOtherRows() {
    if (!this.#groupName) return;

    const accordions = document.querySelectorAll(
      'accordion-custom[data-tmc-accordion-group]'
    );

    accordions.forEach((accordion) => {
      if (
        accordion instanceof AccordionCustom &&
        accordion !== this &&
        accordion.#groupName === this.#groupName &&
        accordion.#expanded
      ) {
        accordion.#animateTo(false);
      }
    });
  }

  #animateTo(expand) {
    const details = this.details;
    const startHeight = details.getBoundingClientRect().height;

    this.#cancelAnimation();
    this.#expanded = expand;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reducedMotion || typeof details.animate !== 'function') {
      details.open = expand;
      return;
    }

    // Mesure la hauteur naturelle de l'état cible.
    details.open = expand;
    const endHeight = details.getBoundingClientRect().height;

    // Maintient le contenu visible pendant son repli.
    details.open = true;

    const animation = details.animate(
      [
        { height: `${startHeight}px` },
        { height: `${endHeight}px` }
      ],
      {
        duration: 750,
        easing: 'cubic-bezier(.45, 0, .55, 1)',
        fill: 'both'
      }
    );

    this.#animation = animation;

    animation.onfinish = () => {
      if (this.#animation !== animation) return;

      details.open = this.#expanded;

      this.#animation = null;
      animation.cancel();
    };
  }

  #cancelAnimation() {
    if (!this.#animation) return;

    this.#animation.onfinish = null;
    this.#animation.cancel();
    this.#animation = null;
  }

  // Synchronise les ouvertures venant d'un autre script,
  // notamment le bouton « Voir plus » de la description.
  #handleToggle = () => {
    if (this.#animation) return;

    this.#expanded = this.details.open;

    if (this.#expanded) {
      this.#closeOtherRows();
    }
  };

  #handleMediaQueryChange = () => {
    this.#cancelAnimation();
    this.#setDefaultOpenState();

    if (this.#isProductAccordion) {
      this.#expanded = this.details.open;

      if (this.#expanded) {
        this.#closeOtherRows();
      }
    }
  };

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#closeWithEscape) return;

    event.preventDefault();

    if (this.#isProductAccordion) {
      this.#animateTo(false);
    } else {
      this.details.open = false;
    }

    this.summary.focus();
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
